import plur from 'plur';
import pluralize from 'pluralize';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { NEVER_REDACTED_ATTRIBUTES } from '@/utils/attributes';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import type { ObjectType } from '@/services/db/object-type/entity';
import type { IdentifiersConfig } from '@/features/people';
import { attributeDeleteBlockReason } from '../utils/bulk-delete';
import { resolveAttributeDescription, resolveEventDescription } from '../utils/reserved-descriptions';
import { getTotalCountForUsageTab, usageRecordsLookup } from '../utils/usage';
import { hasDesignStudioUsage } from '../utils/item';
import { FALLBACK_OBJECT_TYPE_ID } from '../utils/types';
import type { DataIndexItemAddress } from '../urls';
import type { DataIndexItemDetail } from './hooks';
import { withContentUsage, type UsageScope } from '../utils/usage-surfaces';
import type { ReachTerms } from './details-panel';

/**
 * Everything the item page decides before it renders anything: what the item
 * is called, what the workspace calls the things it applies to, how much of
 * the workspace that is, and whether it can be deleted.
 */

export type ItemPageModel = {
  /** The description the page shows, reserved and generated copy included. */
  description: string | null;
  reachTerms: ReachTerms;
  usageScope: UsageScope;
  /** The number on the Usage tab: everything that tab will actually render. */
  usageCount: number;
  /** Why the attribute cannot be deleted, or nothing when it can. */
  deleteBlockReason: string | undefined;
  /** Whether this attribute can be marked sensitive at all. */
  canBeMarkedSensitive: boolean;
  /** Whether the plan allows redacting attributes from teammates at all. */
  canRedact: boolean;
  /** Whether the reach row is shown at all, and whether it carries a share. */
  reach: { withRow: boolean; withShare: boolean };
  campaignIndefinite: string;
  usageTerms: { campaignsTitlePlural: string; newslettersTitlePlural: string };
};

type ItemPageModelInput = {
  address: DataIndexItemAddress;
  item: DataIndexItemDetail;
  objectTypes: readonly ObjectType[];
  identifiers: IdentifiersConfig | null | undefined;
  /** True until the workspace's identifier config has arrived. */
  isEnvironmentLoading: boolean;
  /**
   * Whether this surface's viewer may delete, and what it says when they
   * may not. Both differ by surface in the page this replaces: its item
   * page asks for `journeys/people/edit` and says "You do not have
   * permission…" (`data-index-item/index.gts:466-469`), its drawer asks for
   * `journeys/attribute_metadata/edit` and says "Only workspace admins…"
   * (`data-index-details-drawer/index.gts:177-180`). They arrive together
   * so a surface cannot take one and leave the other.
   */
  deletePermission: { granted: boolean; refusal: string };
};

export function useItemPageModel({
  address,
  item,
  objectTypes,
  identifiers,
  isEnvironmentLoading,
  deletePermission,
}: ItemPageModelInput): ItemPageModel {
  const auth = useAuth();
  // Object and relationship attributes only show content usage where the
  // workspace has it; a person's attributes and events always do.
  const withTemplatesSnippetsLayouts = useFeatureFlag(AccountFeatureFlag.ShowTemplatesSnippetsLayouts);
  const canRedact = auth.account?.planFeatures.sensitive_attributes?.enabled ?? false;

  const objectTypeId = address.scope.kind === 'profile' ? FALLBACK_OBJECT_TYPE_ID : address.scope.objectTypeId;
  const objectType = objectTypes.find((type) => type.id === String(objectTypeId));

  const usageScope: UsageScope = {
    kind: address.kind,
    isBuilder: isBuilderPlan(auth.account),
    withTemplatesSnippetsLayouts,
  };
  const withContent = withContentUsage(usageScope);

  return {
    description: describeItem(address, item),
    reachTerms:
      address.scope.kind === 'profile'
        ? // The lowercase plural mid-sentence, the title-cased singular
          // beside it: "4 people (1%) have this attribute", but "1 Person
          // has this attribute". That asymmetry is the page this replaces,
          // which pluralizes `people.titleSingular` through an inflector
          // whose irregular table stores "people" in lowercase while the
          // singular passes through untouched. Deriving it here instead
          // would not reproduce it — `plur('Person', 2)` is "People", which
          // is what `titlePlural` already said. The label above the sentence
          // capitalizes this, as Ember's does.
          { singular: 'Person', plural: 'people' }
        : objectTypeTerms(objectType?.name),
    usageScope,
    usageCount: getTotalCountForUsageTab(item.usage, {
      withTemplatesSnippetsLayouts: withContent,
      withDesignStudio: hasDesignStudioUsage(address.kind, withContent),
    }),
    deleteBlockReason: !deletePermission.granted
      ? deletePermission.refusal
      : // Which names are identifiers is workspace configuration; until it
        // arrives, every attribute would look deletable.
        isEnvironmentLoading
        ? 'Wait for the workspace settings to load.'
        : attributeDeleteBlockReason(item.name, {
            names: [item.name],
            objectTypeId,
            canEdit: true,
            // The item page's usage arrives with the record it is already
            // showing, so there is no separate read to be waiting on or to
            // have failed.
            isUsageLoading: false,
            isUsageError: false,
            // The detail read carries the records themselves, so the gate reads
            // those rather than asking for the counts a second time.
            countsFor: () => usageRecordsLookup(item.usage),
            identifiers,
            terms: {
              campaignIndefinite: 'an automation',
              campaignsPlural: 'automations',
              newsletterIndefinite: 'a one-time send',
              newslettersPlural: 'one-time sends',
            },
          }),
    canBeMarkedSensitive: address.kind === 'attribute' && canRedact && !NEVER_REDACTED_ATTRIBUTES.includes(item.name),
    canRedact,
    reach: {
      // A relationship attribute describes a link rather than a record, so
      // neither the count nor the share says anything about it.
      withRow: address.kind !== 'object-relationship-attribute',
      // Only an attribute is carried by a share of the workspace; an event
      // is performed, and Ember never gave that a percentage.
      withShare: address.kind !== 'object-relationship-attribute' && address.kind !== 'event',
    },
    campaignIndefinite: 'an automation',
    usageTerms: {
      campaignsTitlePlural: 'Automations',
      newslettersTitlePlural: 'One-time sends',
    },
  };
}

/**
 * What an object type's records are called. The stored name may already be
 * plural ("Companies"), so it is read back to its singular before either
 * form is built — `plur` cannot do that half, it only ever pluralizes.
 */
function objectTypeTerms(name: string | undefined): ReachTerms {
  const singular = pluralize.singular(name ?? 'Object');
  return { singular, plural: plur(singular, 2) };
}

/** What the page shows for a description: written, reserved, or generated. */
export function describeItem(address: DataIndexItemAddress, item: DataIndexItemDetail): string | null {
  const metadata = { description: item.description, generated_description: item.generatedDescription };

  if (address.kind === 'event') {
    return resolveEventDescription(item.name, metadata) ?? null;
  }

  return resolveAttributeDescription(item.name, address.kind, metadata, {
    campaignsPlural: 'automations',
    personSingular: 'person',
    personPlural: 'people',
    customerSingular: 'profile',
  });
}

/**
 * The people or objects list, already narrowed to this item.
 *
 * Built with `generatePath` rather than `buildLocation`: the filter is a
 * base64 string, and `buildLocation` writes search values through
 * `JSON.stringify`, which quotes anything that parses as JSON. `generatePath`
 * writes them as they are, which is what the page reading them expects.
 *
 * The people list keeps its `journeys` segment, as the page this replaces
 * spells it (`item-details/usage/index.gts`, `urlFor('people')`).
 */
export function reachHref(workspaceId: number, address: DataIndexItemAddress, filters: string): string {
  return address.scope.kind === 'profile'
    ? generatePath('/workspaces/:workspaceId/journeys/people', { workspaceId: String(workspaceId) }, { filters })
    : // The objects list is addressed by the object type's slug, which is its
      // id — the same thing the item URL carries.
      generatePath(
        '/workspaces/:workspaceId/journeys/objects/:slug',
        { workspaceId: String(workspaceId), slug: String(address.scope.objectTypeId) },
        { filters },
      );
}
